import React, { useContext, useState, useEffect } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Alert,
  AlertDescription,
  Badge
} from '../components/ui';
import { AlertTriangle, CheckCircle, Utensils, FileText, ExternalLink } from 'lucide-react';
import { USAContext } from './USAContext';
import { useNavigate, useParams } from 'react-router-dom';
import { gapi } from 'gapi-script';

const API_URL = import.meta.env.VITE_API_URL;

// FDA API Configuration  
const FDA_API_BASE_URL = API_URL || (window.location.hostname === 'localhost' 
  ? 'http://localhost:3001'
  : 'https://react-app-production-3114.up.railway.app');

const CLIENT_ID = '513169446517-8t6mm1hc3q2dlfiit3k9n04k4op6s3ih.apps.googleusercontent.com';
const API_KEY = 'AIzaSyDj6U0S0fY66gxnqv6P_VR3LiEcpgvzDzk';
const SPREADSHEET_ID = '1ok2BQYB6FbXXVBVs4uBWRZhMPoMN47k9-Hkj57YYSyE';
const SHEET_NAME = 'Requests'; // Adjust this to your sheet name
const CLIENT_INFO_SHEET_NAME = 'Client Information';
const SCOPES = 'https://www.googleapis.com/auth/spreadsheets';

const FoodFDACheck = () => {
    const { boxes, shipmentItems, shipmentInfo, clientsData, handleNext, selectedPlan } = useContext(USAContext);
    const [hasFood, setHasFood] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [uniqueAsins, setUniqueAsins] = useState(new Set());
    const [submitDisabled, setSubmitDisabled] = useState(true);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMessage, setDialogMessage] = useState('');
    const [submissionMethod, setSubmissionMethod] = useState('api'); // 'sheets' or 'api'
    const [filteredItems, setFilteredItems] = useState([]); // State to hold filtered items
    const [confirmedPlacementOption, setConfirmedPlacementOption] = useState(null);

    const navigate = useNavigate();
    const { shipmentId } = useParams();

    useEffect(() => {
        const initClient = () => {
            gapi.client.init({
                apiKey: API_KEY,
                clientId: CLIENT_ID,
                scope: SCOPES,
                discoveryDocs: ['https://sheets.googleapis.com/$discovery/rest?version=v4'],
            }).then(() => {
                gapi.auth2.getAuthInstance().isSignedIn.listen(updateSigninStatus);
                updateSigninStatus(gapi.auth2.getAuthInstance().isSignedIn.get());
            });
        };

        gapi.load('client:auth2', initClient);
    }, []);

    useEffect(() => {
        if (selectedPlan && selectedPlan.items) {
            // Filter shipmentItems to only include those in selectedPlan.items
            const filtered = shipmentItems.filter(item =>
                selectedPlan.items.some(planItem => planItem.msku === item.item.merchant_sku)
            );
            setFilteredItems(filtered);
        }
    }, [selectedPlan, shipmentItems]);

    useEffect(() => {
        if (selectedPlan) {
            checkPlacementOptions(selectedPlan);
        }
    }, [selectedPlan]);

    const updateSigninStatus = (isSignedIn) => {
        if (isSignedIn) {
            setIsAuthorized(true);
            readSheet();
        } else {
            setIsAuthorized(false);
        }
    };

    const handleAuthClick = () => {
        gapi.auth2.getAuthInstance().signIn();
    };

    const handleSignoutClick = () => {
        gapi.auth2.getAuthInstance().signOut();
    };

    const handleFoodChange = (value) => {
        setHasFood(value === 'yes');
        if (value !== 'yes') {
            setSelectedItems([]); // Clear selected items if "No" is selected
        } else if (value === 'yes' && !isAuthorized) {
            handleAuthClick(); // Prompt authorization if not already authorized
        }
    };

    const handleItemChange = (itemId, checked) => {
        console.log('Selected Item:', itemId);
        if (checked) {
            setSelectedItems([...selectedItems, itemId]);
        } else {
            setSelectedItems(selectedItems.filter((id) => id !== itemId));
        }
        setSubmitDisabled(checked ? false : selectedItems.length <= 1);
    };

    const readSheet = async () => {
        const range = SHEET_NAME; // Adjust to your sheet name
        const response = await gapi.client.sheets.spreadsheets.values.get({
            spreadsheetId: SPREADSHEET_ID,
            range,
        });

        const data = response.result;
        if (data.values && data.values.length > 0) {
            const headers = data.values[0]; // First row is headers
            const rows = data.values.slice(1); // Remaining rows are data

            const parsedData = rows.map(row => {
                const rowData = {};
                headers.forEach((header, index) => {
                    rowData[header] = row[index] || ''; // Default to empty string if value is undefined
                });
                return rowData;
            });

            // Create a unique set of ASINs
            const asins = new Set(parsedData.map(record => record['ASIN OR SKU']));
            setUniqueAsins(asins);
            console.log('Unique ASINs:', asins);
        } else {
            console.log('No data found.');
        }
    };

    const checkIfItemExists = (asin) => {
        return uniqueAsins.has(asin);
    };

    const getClientId = async (clientName) => {
        const response = await gapi.client.sheets.spreadsheets.values.get({
            spreadsheetId: SPREADSHEET_ID,
            range: CLIENT_INFO_SHEET_NAME,
        });

        const data = response.result;
        if (data.values && data.values.length > 0) {
            const headers = data.values[0]; // First row is headers
            const rows = data.values.slice(1); // Remaining rows are data

            const parsedData = rows.map(row => {
                const rowData = {};
                headers.forEach((header, index) => {
                    rowData[header] = row[index] || ''; // Default to empty string if value is undefined
                });
                return rowData;
            });

            const clientInfo = parsedData.find(client => client['Company Name'] === clientName);
            return clientInfo ? clientInfo['Client ID'] : null;
        } else {
            console.log('No client data found.');
            return null;
        }
    };

    const checkPlacementOptions = (plan) => {
        if (!plan || !plan.placement_options || plan.placement_options.length === 0) {
            console.log('No placement options available.');
            return;
        }

        const confirmedOptions = plan.placement_options.filter(option => option.confirmed_at);
        if (confirmedOptions.length === 0) {
            console.log('No placement option has been confirmed.');
            return;
        }

        const latestConfirmedOption = confirmedOptions.reduce((latest, current) => {
            return new Date(current.confirmed_at) > new Date(latest.confirmed_at) ? current : latest;
        });

        setConfirmedPlacementOption(latestConfirmedOption);
    };

    const findShipmentForBox = (box) => {
        if (!confirmedPlacementOption || !confirmedPlacementOption.shipments) {
            console.log('No confirmed placement option or shipments found.');
            return null;
        }
    
        const boxNumberInt = parseInt(box.boxNumber, 10);
        if (isNaN(boxNumberInt)) {
            console.error(`Invalid boxNumber: ${box.boxNumber}`);
            return null;
        }
    
        for (const shipment of confirmedPlacementOption.shipments) {
            console.log(`Checking shipment ID: ${shipment.id}`);
            
            // First try to find the box in box_groups directly
            for (const boxGroup of shipment.box_groups) {
                if (Array.isArray(boxGroup.box_numbers) && boxGroup.box_numbers.length > 0) {
                    if (boxGroup.box_numbers.includes(boxNumberInt)) {
                        console.log(`Box number ${boxNumberInt} found in shipment ID: ${shipment.id}`);
                        return shipment;
                    }
                }
            }
            
            // If not found and box_numbers arrays are empty, check shipment.boxes
            if (shipment.boxes && Array.isArray(shipment.boxes)) {
                const matchingBox = shipment.boxes.find(b => b.box_number === boxNumberInt);
                if (matchingBox) {
                    console.log(`Found matching box in shipment.boxes with box_group_id: ${matchingBox.box_group_id}`);
                    
                    // Find the corresponding box_group using the box_group_id
                    const matchingBoxGroup = shipment.box_groups.find(bg => bg.id === matchingBox.box_group_id);
                    if (matchingBoxGroup) {
                        console.log(`Found matching box group in shipment`);
                        return shipment;
                    }
                }
            }
        }
    
        console.log(`No matching shipment found for box number: ${boxNumberInt}`);
        return null;
    };
    

    const handleSubmit = async () => {
        if (hasFood && selectedItems.length > 0) {
            try {
                const clientData = clientsData.find(client => client.id === shipmentInfo.team_id);
                if (!clientData) {
                    console.error('Client data not found.');
                    return;
                }
                
                if (submissionMethod === 'api') {
                    await handleApiSubmission(clientData);
                } else {
                    await handleSheetsSubmission(clientData);
                }
            } catch (error) {
                console.error('Error in submission:', error);
                alert('Submission failed: ' + error.message);
            }
        } else {
            handleNext();
        }
    };

    const handleSheetsSubmission = async (clientData) => {
        const clientId = await getClientId(clientData.name);
        if (!clientId) {
            console.error('Client ID not found.');
            alert('Client ID not found, might need to be added to Google Sheets first.');
            return;
        }

        const rows = [];
                const now = new Date();
                const dateRequested = now.toISOString(); // Full date and time
                const dateRequestedTime = ''; // Empty since it's already included in dateRequested

                console.log("DEBUG - Selected Items:", selectedItems);
                console.log("DEBUG - Boxes:", boxes);
                console.log("DEBUG - Selected Plan:", selectedPlan);

                selectedItems.forEach(itemId => {
                    console.log("DEBUG - Processing Item ID:", itemId);
                    const item = shipmentItems.find(item => item.id == itemId);
                    console.log("DEBUG - Found Item:", item);
                    
                    if (!item) {
                        console.log("DEBUG - Item not found in shipmentItems!");
                        return;
                    }

                    const isExisting = checkIfItemExists(item.item.asin);
                    console.log("DEBUG - Is Existing Item:", isExisting);
                    
                    boxes.forEach(box => {
                        console.log("DEBUG - Processing Box:", box);
                        const shipment = findShipmentForBox(box);
                        console.log("DEBUG - Found Shipment:", shipment);
                        
                        const matchingBoxItem = box.items.find(boxItem => {
                            console.log("DEBUG - Comparing SKUs:");
                            console.log("Box Item SKU:", boxItem.SKU);
                            console.log("Merchant SKU:", item.item.merchant_sku);
                            return boxItem.SKU === item.item.merchant_sku;
                        });
                        console.log("DEBUG - Matching Box Item:", matchingBoxItem);

                        if (shipment && matchingBoxItem) {
                            console.log("DEBUG - Adding row for box", box.boxNumber, "and item", item.item.asin);
                            rows.push([
                                selectedPlan.inbound_plan_id.substring(selectedPlan.inbound_plan_id.length - 4),
                                dateRequested,
                                '', // Date Completed
                                dateRequestedTime,
                                'Pending',
                                clientId,
                                box.boxNumber,
                                '',
                                'Amazon FBA',
                                shipment.destination.address.address_line_1,
                                shipment.destination.address.address_line_2 || '',
                                shipment.destination.address.address_line_3 || '',
                                shipment.destination.address.city,
                                shipment.destination.address.state_or_province_code,
                                shipment.destination.address.postal_code,
                                box.items.find(boxItem => boxItem.ASIN == item.item.asin).QTY, // Amount
                                item.item.asin,
                                '',
                                2,
                                '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''
                            ]);
                        } else {
                            console.log("Asin no match")
                            console.log("Box items:")
                            console.log(box.items)
                            console.log("ITEM:")
                            console.log(item)
                        }
                    });
                });

                console.log('Rows to Append:', rows);

                if (rows.length === 0) {
                    console.log("DEBUG - No rows were generated! Checking conditions:");
                    console.log("- hasFood:", hasFood);
                    console.log("- selectedItems length:", selectedItems.length);
                    console.log("- boxes length:", boxes.length);
                    console.log("- shipmentItems length:", shipmentItems.length);
                    return;
                }

                try {
                const response = await gapi.client.sheets.spreadsheets.values.append({
                    spreadsheetId: SPREADSHEET_ID,
                    range: SHEET_NAME,
                    valueInputOption: 'RAW',
                    insertDataOption: 'INSERT_ROWS',
                    resource: {
                        values: rows,
                    },
                });

                console.log("DEBUG - Google Sheets API Response:", response);

                if (!response.status === 200) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                console.log('Google Sheets Append Response:', response);

                setDialogMessage(`${rows.length} rows added to the sheet. You can view the sheet `);
                setDialogOpen(true);
            } catch (error) {
                console.error('Error inserting data into Google Sheets:', error);
            }
    };

    const handleApiSubmission = async (clientData) => {
        try {
            // Prepare FDA submission data
            const boxesForSubmission = [];
            const processedBoxes = new Set(); // Track processed boxes to avoid duplicates
            
            selectedItems.forEach(itemId => {
                const item = shipmentItems.find(item => item.id == itemId);
                if (!item) return;

                boxes.forEach(box => {
                    const shipment = findShipmentForBox(box);
                    const matchingBoxItem = box.items.find(boxItem => boxItem.SKU === item.item.merchant_sku);

                    if (shipment && matchingBoxItem && !processedBoxes.has(box.boxNumber)) {
                        processedBoxes.add(box.boxNumber);
                        
                        // Collect all selected products in this box
                        const productsInBox = [];
                        selectedItems.forEach(selectedItemId => {
                            const selectedItem = shipmentItems.find(si => si.id == selectedItemId);
                            if (!selectedItem) return;
                            
                            const boxItem = box.items.find(bi => bi.SKU === selectedItem.item.merchant_sku);
                            if (boxItem) {
                                productsInBox.push({
                                    asin: selectedItem.item.asin,
                                    quantity: boxItem.QTY || 1
                                });
                            }
                        });
                        
                        if (productsInBox.length > 0) {
                            boxesForSubmission.push({
                                boxId: `BOX-${box.boxNumber}`,
                                submitter: {
                                    businessName: clientData.name,
                                    firstName: clientData.name.split(' ')[0] || 'N/A',
                                    lastName: clientData.name.split(' ').slice(1).join(' ') || 'N/A',
                                    email: clientData.email || 'noreply@example.com',
                                    phoneNumber: clientData.phone || '0000000000',
                                    streetAddress: clientData.address || 'N/A',
                                    city: clientData.city || 'N/A',
                                    state: clientData.state || 'N/A',
                                    zipCode: clientData.zip || '00000',
                                    countryCode: 'CA'
                                },
                                recipient: {
                                    businessName: 'Amazon FBA Warehouse',
                                    streetAddress: shipment.destination.address.address_line_1,
                                    city: shipment.destination.address.city,
                                    state: shipment.destination.address.state_or_province_code,
                                    zipCode: shipment.destination.address.postal_code,
                                    countryCode: 'US'
                                },
                                products: productsInBox
                            });
                        }
                    }
                });
            });

            const requestPayload = {
                boxes: boxesForSubmission,
                options: {
                    priority: 'normal',
                    notificationEmail: clientData.email || 'noreply@example.com'
                }
            };

            console.log('Submitting to FDA API with payload:', requestPayload);
            console.log(`DEBUG: Submitting ${boxesForSubmission.length} boxes to FDA API`);

            const response = await fetch(`${FDA_API_BASE_URL}/api/fda/submit-boxes`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestPayload),
            });

            if (!response.ok) {
                throw new Error(`FDA API error: ${response.status} ${response.statusText}`);
            }

            const result = await response.json();
            console.log('FDA API Response:', result);

            setDialogMessage(`FDA submission successful! ${boxesForSubmission.length} boxes submitted.`);
            setDialogOpen(true);

        } catch (error) {
            console.error('Error submitting to FDA API:', error);
            throw error;
        }
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        handleNext();
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center space-x-2 mb-6">
                <Utensils className="w-6 h-6 text-orange-600" />
                <h2 className="text-2xl font-bold text-slate-900">Food/FDA Check</h2>
            </div>

            <Card>
                <CardHeader>
                    <h3 className="text-lg font-semibold">Are there any food/FDA products?</h3>
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* Radio Group */}
                    <div className="flex space-x-6">
                        <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                                type="radio"
                                name="hasFood"
                                value="yes"
                                checked={hasFood}
                                onChange={(e) => handleFoodChange(e.target.value)}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium text-gray-900">Yes</span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                                type="radio"
                                name="hasFood"
                                value="no"
                                checked={!hasFood}
                                onChange={(e) => handleFoodChange(e.target.value)}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium text-gray-900">No</span>
                        </label>
                    </div>

                    {/* Authorization Status */}
                    {hasFood && (
                        <div className="flex items-center space-x-2">
                            {isAuthorized ? (
                                <div className="flex items-center space-x-2 text-green-600">
                                    <CheckCircle className="w-4 h-4" />
                                    <span className="text-sm font-medium">Google Sheets Authorized</span>
                                </div>
                            ) : (
                                <div className="flex items-center space-x-2 text-orange-600">
                                    <AlertTriangle className="w-4 h-4" />
                                    <span className="text-sm font-medium">Authorization Required</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Submission Method Selection */}
                    {hasFood && (
                        <div className="space-y-4">
                            <h4 className="text-md font-medium text-slate-900">Submission Method</h4>
                            <div className="flex space-x-6">
                                <label className="flex items-center space-x-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="submissionMethod"
                                        value="api"
                                        checked={submissionMethod === 'api'}
                                        onChange={(e) => setSubmissionMethod(e.target.value)}
                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-sm font-medium text-gray-900">FDA API (Recommended)</span>
                                </label>
                                <label className="flex items-center space-x-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="submissionMethod"
                                        value="sheets"
                                        checked={submissionMethod === 'sheets'}
                                        onChange={(e) => setSubmissionMethod(e.target.value)}
                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-sm font-medium text-gray-900">Google Sheets</span>
                                </label>
                            </div>
                        </div>
                    )}

                    {/* Food Products Selection */}
                    {((submissionMethod === 'api' && hasFood) || (submissionMethod === 'sheets' && isAuthorized && hasFood)) && (
                        <div className="space-y-4">
                            <h4 className="text-md font-medium text-slate-900">Select Food Products</h4>
                            <div className="space-y-3">
                                {filteredItems.map((item, index) => (
                                    <div key={index} className="border border-slate-200 rounded-lg p-4">
                                        <label className="flex items-start space-x-3 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                value={item.id}
                                                onChange={(e) => handleItemChange(item.id, e.target.checked)}
                                                className="mt-1 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                            <div className="flex items-start space-x-3 flex-1">
                                                <div className="flex-shrink-0">
                                                    <img 
                                                        src={(item.item.images && item.item.images.length > 0 && item.item.images[0].large_url) || 'https://qph.cf2.quoracdn.net/main-qimg-654617264f9192ec976abe6e53356240-lq'}
                                                        alt={item.item.title} 
                                                        className="w-12 h-12 object-cover rounded border border-slate-200"
                                                    />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-slate-900">{item.item.title}</p>
                                                    <div className="mt-1 text-xs text-slate-500 space-y-1">
                                                        {item.item.merchant_sku && (
                                                            <p>Merchant SKU: {item.item.merchant_sku}</p>
                                                        )}
                                                        {item.item.asin && (
                                                            <p>ASIN: {item.item.asin}</p>
                                                        )}
                                                        {item.item.fnsku && (
                                                            <p>FNSKU: {item.item.fnsku}</p>
                                                        )}
                                                    </div>
                                                    {checkIfItemExists(item.item.asin) && (
                                                        <Badge variant="secondary" className="mt-2 text-xs">
                                                            Has had PN before
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex space-x-3 pt-4">
                        {hasFood && submissionMethod === 'api' && (
                            <Button
                                onClick={handleSubmit}
                                disabled={submitDisabled}
                                className="bg-blue-600 hover:bg-blue-700 flex items-center space-x-2"
                            >
                                <FileText className="w-4 h-4" />
                                <span>Submit to FDA API</span>
                            </Button>
                        )}

                        {hasFood && submissionMethod === 'sheets' && isAuthorized && (
                            <Button
                                onClick={handleSubmit}
                                disabled={submitDisabled}
                                className="bg-blue-600 hover:bg-blue-700 flex items-center space-x-2"
                            >
                                <FileText className="w-4 h-4" />
                                <span>Submit to Sheet</span>
                            </Button>
                        )}

                        {hasFood && submissionMethod === 'sheets' && !isAuthorized && (
                            <Button
                                onClick={handleAuthClick}
                                className="bg-green-600 hover:bg-green-700 flex items-center space-x-2"
                            >
                                <ExternalLink className="w-4 h-4" />
                                <span>Authorize Google Sheets</span>
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Success Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center space-x-2">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <span>Success</span>
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-slate-600">
                            {dialogMessage}
                            <a 
                                href={`https://docs.google.com/spreadsheets/d/${SPREADSHEET_ID}?range=A7308`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 underline ml-1"
                            >
                                here
                            </a>.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button onClick={handleDialogClose} className="bg-blue-600 hover:bg-blue-700">
                            OK
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default FoodFDACheck;